/**
 * pdf.js, loaded only when a PDF page is being marked up (FILES-EDIT-01).
 * The browser build lives in public/vendor (Apache-2.0, Mozilla) so nothing
 * new has to be installed and the app never waits on a CDN. Only the few
 * calls Pegasus uses are typed here.
 */
const BASE = "/vendor/pdfjs-4.10.38";

export interface PdfPage {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number } }): { promise: Promise<void> };
}
export interface PdfDoc {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
  destroy(): Promise<void>;
}
interface PdfJs {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(src: { data: Uint8Array }): { promise: Promise<PdfDoc> };
}

let loading: Promise<PdfJs> | null = null;

export function loadPdfJs(): Promise<PdfJs> {
  if (!loading) {
    loading = (import(/* @vite-ignore */ `${BASE}/pdf.min.mjs`) as Promise<PdfJs>).then((m) => {
      m.GlobalWorkerOptions.workerSrc = `${BASE}/pdf.worker.min.mjs`;
      return m;
    });
    loading.catch(() => { loading = null; });
  }
  return loading;
}

/** Open a PDF from its bytes. */
export async function openPdf(bytes: Uint8Array): Promise<PdfDoc> {
  const pdfjs = await loadPdfJs();
  return pdfjs.getDocument({ data: bytes }).promise;
}

/** Draw one page onto a new canvas about `width` pixels wide (sharp enough to mark up plans). */
export async function renderPdfPage(doc: PdfDoc, n: number, width = 2000): Promise<HTMLCanvasElement> {
  const page = await doc.getPage(n);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(4, Math.max(0.5, width / base.width));
  const vp = page.getViewport({ scale });
  const c = document.createElement("canvas");
  c.width = Math.round(vp.width);
  c.height = Math.round(vp.height);
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("No canvas");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, c.width, c.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return c;
}
